import React, { useEffect, useRef, useState } from "react";
import {
    View,
    Text,
    TouchableOpacity,
    Image,
    Animated,
    Dimensions,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppImages } from "../../utility/AppImages";
import Avatar from "../choose-avatar-components/Avatar";
import { useNavigation } from "@react-navigation/native";
import { navigateToScreen } from "../../utility/NavigationHelper";
import { AppScreens } from "../../screens/Utility/AppScreens";

const { height: screenHeight, width: screenWidth } = Dimensions.get("window");

interface ChallengeResponsePopupProps {
    visible: boolean;
    onClose: () => void;
    onViewEvent: () => void;
    notificationData: {
        actionStatus: "accepted" | "rejected";
        message: string;
        details: {
            challengerId: string;
            gameTitle: string;
            eventTitle: string;
            eventStartTime: string;
            eventEndTime: string;
            eventID: string;
            participantProfiles?: Array<{
                userId: string;
                profilePicture: string;
            }>;
        };
    } | null;
}

const ChallengeResponsePopup: React.FC<ChallengeResponsePopupProps> = ({
    visible,
    onClose,
    onViewEvent,
    notificationData,
}) => {
    const isMobile = screenWidth < 768;
    // Desktop: slide from right (screenWidth), Mobile: slide from bottom (screenHeight)
    const slideAnim = useRef(new Animated.Value(isMobile ? screenHeight : screenWidth)).current;
    const [countdown, setCountdown] = useState(5);
    const countdownRef = useRef<NodeJS.Timeout | null>(null);
    const navigation = useNavigation();

    const isAccepted = notificationData?.actionStatus === "accepted";

    // Get challenger profile from participantProfiles
    const getChallengerProfile = () => {
        if (!notificationData?.details?.participantProfiles) return null;
        return notificationData.details.participantProfiles.find(
            (p) => p.userId === notificationData.details.challengerId
        );
    };

    const challengerProfile = getChallengerProfile();

    // Format event date/time
    const formatEventDateTime = () => {
        if (!notificationData?.details?.eventStartTime) return "";
        const startDate = new Date(notificationData.details.eventStartTime);
        const endDate = notificationData.details.eventEndTime
            ? new Date(notificationData.details.eventEndTime)
            : null;

        const dateStr = startDate.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
        });

        const startTime = startDate.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });

        const endTime = endDate
            ? endDate.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            })
            : "";

        return `${dateStr} ${startTime}${endTime ? `-${endTime}` : ""}`;
    };

    // Countdown timer
    useEffect(() => {
        if (visible) {
            setCountdown(5);
            countdownRef.current = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        if (countdownRef.current) {
                            clearInterval(countdownRef.current);
                        }
                        onClose();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (countdownRef.current) {
                clearInterval(countdownRef.current);
            }
        };
    }, [visible, onClose]);

    // Slide animation - Desktop: from right, Mobile: from bottom
    useEffect(() => {
        const offScreenValue = isMobile ? screenHeight : screenWidth;
        if (visible) {
            Animated.spring(slideAnim, {
                toValue: 0,
                useNativeDriver: true,
                tension: 65,
                friction: 10,
            }).start();
        } else {
            Animated.timing(slideAnim, {
                toValue: offScreenValue,
                duration: 250,
                useNativeDriver: true,
            }).start();
        }
    }, [visible]);

    if (!visible || !notificationData) {
        return null;
    }

    const handleViewEvent = () => {
        onClose();
        navigateToScreen(navigation, AppScreens.OnGoingEvents, {
            eventID: notificationData.details.eventID,
        });
    };

    // Theme colors - same red color for both (matching challenge received popup)
    const themeColors = {
        gradientColors: ["#400000", "#291B1B"] as const,
        borderColor: AppColors.koBrandRed,
        titleColor: AppColors.koBrandRed,
        actionButtonBg: AppColors.koBrandRed,
        actionText: isAccepted ? "accepted your challenge" : "declined your challenge",
    };

    // Desktop popup - REJECTED (Simple layout)
    if (!isMobile && !isAccepted) {
        return (
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9999,
                }}
                pointerEvents="box-none"
            >
                <Animated.View
                    style={{
                        position: "absolute",
                        bottom: 16,
                        right: 16,
                        width: 400,
                        borderRadius: 8,
                        transform: [{ translateX: slideAnim }],
                        shadowColor: "#000",
                        shadowOffset: { width: 0, height: 4 },
                        shadowOpacity: 0.3,
                        shadowRadius: 8,
                        elevation: 10,
                    }}
                >
                    <LinearGradient
                        colors={themeColors.gradientColors}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={{
                            borderRadius: 8,
                            borderWidth: 2,
                            borderColor: themeColors.borderColor,
                            overflow: "hidden",
                            padding: 16,
                        }}
                    >
                        {/* Header */}
                        <View
                            style={{
                                flexDirection: "row",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <View
                                style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
                            >
                                <Image
                                    source={AppImages.icChallengeIcon}
                                    style={{ width: 32, height: 32 }}
                                    resizeMode="contain"
                                />
                                <Text
                                    style={[
                                        AppFontStyle.BOLD_ANYBODY_16,
                                        { color: themeColors.titleColor },
                                    ]}
                                >
                                    Challenge Declined
                                </Text>
                            </View>
                            <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
                                <Text
                                    style={[
                                        AppFontStyle.BOLD_ANYBODY_16,
                                        { color: themeColors.titleColor },
                                    ]}
                                >
                                    {countdown}
                                </Text>
                                <TouchableOpacity onPress={onClose}>
                                    <Image
                                        source={AppImages.icClose}
                                        style={{ width: 20, height: 20, tintColor: "white" }}
                                        resizeMode="contain"
                                    />
                                </TouchableOpacity>
                            </View>
                        </View>

                        {/* Subtitle */}
                        <View style={{ marginTop: 12 }}>
                            <Text style={{ fontSize: 14 }}>
                                <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: "white" }]}>
                                    @{notificationData.details.challengerId}
                                </Text>
                                <Text
                                    style={[
                                        AppFontStyle.REGULAR_ANYBODY_14,
                                        { color: "#FFD700" },
                                    ]}
                                >
                                    {"  "}declined your challenge
                                </Text>
                            </Text>
                        </View>

                        {/* Simple Message */}
                        <Text
                            style={[
                                AppFontStyle.REGULAR_ANYBODY_14,
                                { color: "white", marginTop: 16 },
                            ]}
                        >
                            They're sitting this one out. Try invite other opponent or another match?
                        </Text>
                    </LinearGradient>
                </Animated.View>
            </View>
        );
    }

    // Desktop popup - ACCEPTED (Full layout with profile and buttons)
    if (!isMobile) {
        return (
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9999,
                }}
                pointerEvents="box-none"
            >
                <Animated.View
                    style={{
                        position: "absolute",
                        bottom: 16,
                        right: 16,
                        width: 400,
                        borderRadius: 8,
                        transform: [{ translateX: slideAnim }],
                        shadowColor: "#000",
                        shadowOffset: { width: 0, height: 4 },
                        shadowOpacity: 0.3,
                        shadowRadius: 8,
                        elevation: 10,
                    }}
                >
                    <LinearGradient
                        colors={themeColors.gradientColors}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={{
                            borderRadius: 8,
                            borderWidth: 2,
                            borderColor: themeColors.borderColor,
                            overflow: "hidden",
                        }}
                    >
                        {/* Header */}
                        <View
                            style={{
                                paddingTop: 12,
                                paddingHorizontal: 16,
                                flexDirection: "row",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <View
                                style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
                            >
                                <Image
                                    source={AppImages.icChallengeIcon}
                                    style={{ width: 32, height: 32 }}
                                    resizeMode="contain"
                                />
                                <Text
                                    style={[
                                        AppFontStyle.BOLD_ANYBODY_16,
                                        { color: themeColors.titleColor },
                                    ]}
                                >
                                    Challenge Accepted
                                </Text>
                            </View>
                            <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
                                <Text
                                    style={[
                                        AppFontStyle.BOLD_ANYBODY_16,
                                        { color: themeColors.titleColor },
                                    ]}
                                >
                                    {countdown}
                                </Text>
                                <TouchableOpacity onPress={onClose}>
                                    <Image
                                        source={AppImages.icClose}
                                        style={{ width: 20, height: 20, tintColor: "white" }}
                                        resizeMode="contain"
                                    />
                                </TouchableOpacity>
                            </View>
                        </View>

                        {/* Subtitle */}
                        <View style={{ paddingHorizontal: 16, paddingTop: 8 }}>
                            <Text style={{ fontSize: 14 }}>
                                <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: "white" }]}>
                                    @{notificationData.details.challengerId}
                                </Text>
                                <Text
                                    style={[
                                        AppFontStyle.REGULAR_ANYBODY_14,
                                        { color: themeColors.titleColor, marginLeft: 8 },
                                    ]}
                                >
                                    {"   "}
                                    {themeColors.actionText}
                                </Text>
                            </Text>
                        </View>

                        {/* Content */}
                        <View style={{ padding: 12 }}>
                            {/* Challenger Info Card */}
                            <View
                                style={{
                                    backgroundColor: "rgba(0, 0, 0, 0.3)",
                                    borderRadius: 8,
                                    padding: 12,
                                    marginBottom: 12,
                                }}
                            >
                                <View
                                    style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                    }}
                                >
                                    <View
                                        style={{
                                            width: 50,
                                            height: 50,
                                            borderRadius: 25,
                                            borderWidth: 2,
                                            borderColor: themeColors.borderColor,
                                            marginRight: 12,
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            overflow: "hidden",
                                        }}
                                    >
                                        <Avatar
                                            src={challengerProfile?.profilePicture || ""}
                                            variant="profile"
                                        />
                                    </View>
                                    <View style={{ flex: 1 }}>
                                        <Text
                                            style={[AppFontStyle.BOLD_ANYBODY_16, { color: "white" }]}
                                            numberOfLines={1}
                                        >
                                            {notificationData.details.challengerId}
                                        </Text>
                                        <Text
                                            style={[
                                                AppFontStyle.REGULAR_ANYBODY_12,
                                                { color: "#CCCCCC", marginTop: 2 },
                                            ]}
                                            numberOfLines={1}
                                        >
                                            •{notificationData.details.gameTitle} •{formatEventDateTime()}
                                        </Text>
                                        <Text
                                            style={[
                                                AppFontStyle.REGULAR_ANYBODY_14,
                                                { color: "white", marginTop: 6 },
                                            ]}
                                            numberOfLines={2}
                                        >
                                            {notificationData.details.eventTitle}
                                        </Text>
                                    </View>
                                </View>
                            </View>

                            {/* Action Buttons */}
                            <View style={{ flexDirection: "row", gap: 10 }}>
                                <TouchableOpacity onPress={onClose} style={{ flex: 1 }}>
                                    <View
                                        style={{
                                            backgroundColor: "transparent",
                                            borderWidth: 1,
                                            borderColor: "white",
                                            borderRadius: 8,
                                            paddingVertical: 12,
                                            alignItems: "center",
                                        }}
                                    >
                                        <Text
                                            style={[AppFontStyle.BOLD_ANYBODY_16, { color: "white" }]}
                                        >
                                            Dismiss
                                        </Text>
                                    </View>
                                </TouchableOpacity>
                                <TouchableOpacity onPress={handleViewEvent} style={{ flex: 1 }}>
                                    <View
                                        style={{
                                            backgroundColor: themeColors.actionButtonBg,
                                            borderRadius: 8,
                                            paddingVertical: 12,
                                            alignItems: "center",
                                        }}
                                    >
                                        <Text
                                            style={[AppFontStyle.BOLD_ANYBODY_16, { color: "white" }]}
                                        >
                                            View event
                                        </Text>
                                    </View>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </LinearGradient>
                </Animated.View>
            </View>
        );
    }

    // Mobile bottom sheet - REJECTED (Simple layout)
    if (!isAccepted) {
        return (
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9999,
                }}
                pointerEvents="box-none"
            >
                <Animated.View
                    style={{
                        position: "absolute",
                        bottom: 60,
                        left: 8,
                        right: 8,
                        borderRadius: 12,
                        transform: [{ translateY: slideAnim }],
                        overflow: "hidden",
                        shadowColor: "#000",
                        shadowOffset: { width: 0, height: 4 },
                        shadowOpacity: 0.3,
                        shadowRadius: 8,
                        elevation: 10,
                    }}
                >
                    <LinearGradient
                        colors={themeColors.gradientColors}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={{
                            borderRadius: 12,
                            borderWidth: 2,
                            borderColor: themeColors.borderColor,
                            padding: 12,
                        }}
                    >
                        {/* Header */}
                        <View
                            style={{
                                flexDirection: "row",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <View
                                style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
                            >
                                <Image
                                    source={AppImages.icChallengeIcon}
                                    style={{ width: 20, height: 20 }}
                                    resizeMode="contain"
                                />
                                <Text
                                    style={[
                                        AppFontStyle.BOLD_ANYBODY_12,
                                        { color: themeColors.titleColor },
                                    ]}
                                >
                                    Challenge Declined
                                </Text>
                            </View>
                            <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                                <Text
                                    style={[
                                        AppFontStyle.BOLD_ANYBODY_12,
                                        { color: themeColors.titleColor },
                                    ]}
                                >
                                    {countdown}
                                </Text>
                                <TouchableOpacity onPress={onClose}>
                                    <Image
                                        source={AppImages.icClose}
                                        style={{ width: 14, height: 14, tintColor: "white" }}
                                        resizeMode="contain"
                                    />
                                </TouchableOpacity>
                            </View>
                        </View>

                        {/* Subtitle */}
                        <View style={{ marginTop: 8 }}>
                            <Text style={{ fontSize: 11 }}>
                                <Text style={[AppFontStyle.BOLD_ANYBODY_12, { color: "white" }]}>
                                    @{notificationData.details.challengerId}
                                </Text>
                                <Text
                                    style={[
                                        AppFontStyle.REGULAR_ANYBODY_12,
                                        { color: "#FFD700" },
                                    ]}
                                >
                                    {"  "}declined your challenge
                                </Text>
                            </Text>
                        </View>

                        {/* Simple Message */}
                        <Text
                            style={[
                                AppFontStyle.REGULAR_ANYBODY_12,
                                { color: "white", marginTop: 8 },
                            ]}
                        >
                            They're sitting this one out. Try invite other opponent or another match?
                        </Text>
                    </LinearGradient>
                </Animated.View>
            </View>
        );
    }

    // Mobile bottom sheet - ACCEPTED (Full layout)
    return (
        <View
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
            }}
            pointerEvents="box-none"
        >
            <Animated.View
                style={{
                    position: "absolute",
                    bottom: 60,
                    left: 8,
                    right: 8,
                    borderRadius: 12,
                    transform: [{ translateY: slideAnim }],
                    overflow: "hidden",
                    shadowColor: "#000",
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.3,
                    shadowRadius: 8,
                    elevation: 10,
                }}
            >
                <LinearGradient
                    colors={themeColors.gradientColors}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={{
                        borderRadius: 12,
                        borderWidth: 2,
                        borderColor: themeColors.borderColor,
                    }}
                >
                    {/* Header */}
                    <View
                        style={{
                            paddingTop: 12,
                            paddingHorizontal: 12,
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <View
                            style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
                        >
                            <Image
                                source={AppImages.icChallengeIcon}
                                style={{ width: 24, height: 24 }}
                                resizeMode="contain"
                            />
                            <Text
                                style={[
                                    AppFontStyle.BOLD_ANYBODY_14,
                                    { color: themeColors.titleColor },
                                ]}
                            >
                                Challenge Accepted
                            </Text>
                        </View>
                        <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
                            <Text
                                style={[
                                    AppFontStyle.BOLD_ANYBODY_14,
                                    { color: themeColors.titleColor },
                                ]}
                            >
                                {countdown}
                            </Text>
                            <TouchableOpacity onPress={onClose}>
                                <Image
                                    source={AppImages.icClose}
                                    style={{ width: 16, height: 16, tintColor: "white" }}
                                    resizeMode="contain"
                                />
                            </TouchableOpacity>
                        </View>
                    </View>

                    {/* Subtitle */}
                    <View style={{ paddingHorizontal: 12, paddingTop: 6 }}>
                        <Text style={{ fontSize: 12 }}>
                            <Text style={[AppFontStyle.BOLD_ANYBODY_12, { color: "white" }]}>
                                @{notificationData.details.challengerId}
                            </Text>
                            <Text
                                style={[
                                    AppFontStyle.REGULAR_ANYBODY_12,
                                    { color: themeColors.titleColor },
                                ]}
                            >
                                {"   "}
                                {themeColors.actionText}
                            </Text>
                        </Text>
                    </View>

                    {/* Content */}
                    <View style={{ padding: 12 }}>
                        {/* Challenger Info Card */}
                        <View
                            style={{
                                backgroundColor: "rgba(0, 0, 0, 0.3)",
                                borderRadius: 8,
                                padding: 12,
                                marginBottom: 10,
                            }}
                        >
                            <View
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                }}
                            >
                                <View
                                    style={{
                                        width: 45,
                                        height: 45,
                                        borderRadius: 22.5,
                                        borderWidth: 2,
                                        borderColor: themeColors.borderColor,
                                        marginRight: 10,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        overflow: "hidden",
                                    }}
                                >
                                    <Avatar
                                        src={challengerProfile?.profilePicture || ""}
                                        variant="profile"
                                    />
                                </View>
                                <View style={{ flex: 1 }}>
                                    <Text
                                        style={[AppFontStyle.BOLD_ANYBODY_14, { color: "white" }]}
                                        numberOfLines={1}
                                    >
                                        {notificationData.details.challengerId}
                                    </Text>
                                    <Text
                                        style={[
                                            AppFontStyle.REGULAR_ANYBODY_10,
                                            { color: "#CCCCCC", marginTop: 2 },
                                        ]}
                                        numberOfLines={1}
                                    >
                                        •{notificationData.details.gameTitle} •{formatEventDateTime()}
                                    </Text>
                                    <Text
                                        style={[
                                            AppFontStyle.REGULAR_ANYBODY_12,
                                            { color: "white", marginTop: 4 },
                                        ]}
                                        numberOfLines={2}
                                    >
                                        {notificationData.details.eventTitle}
                                    </Text>
                                </View>
                            </View>
                        </View>

                        {/* Action Buttons */}
                        <View style={{ flexDirection: "row", gap: 10 }}>
                            <TouchableOpacity onPress={onClose} style={{ flex: 1 }}>
                                <View
                                    style={{
                                        backgroundColor: "transparent",
                                        borderWidth: 1,
                                        borderColor: "white",
                                        borderRadius: 8,
                                        paddingVertical: 10,
                                        alignItems: "center",
                                    }}
                                >
                                    <Text
                                        style={[AppFontStyle.BOLD_ANYBODY_14, { color: "white" }]}
                                    >
                                        Dismiss
                                    </Text>
                                </View>
                            </TouchableOpacity>
                            <TouchableOpacity onPress={handleViewEvent} style={{ flex: 1 }}>
                                <View
                                    style={{
                                        backgroundColor: themeColors.actionButtonBg,
                                        borderRadius: 8,
                                        paddingVertical: 10,
                                        alignItems: "center",
                                    }}
                                >
                                    <Text
                                        style={[AppFontStyle.BOLD_ANYBODY_14, { color: "white" }]}
                                    >
                                        View event
                                    </Text>
                                </View>
                            </TouchableOpacity>
                        </View>
                    </View>
                </LinearGradient>
            </Animated.View>
        </View>
    );
};

export default ChallengeResponsePopup;
